import type { components } from '@tallerya/api-client';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useState, type ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { api } from '../api';
import { useTallerDeLaCache } from '../taller/ProveedorTaller';

export type MiSucursal = components['schemas']['MyLocation'];

// La sucursal en la que se está trabajando (#101): la lista sale de
// `GET /api/me/locations` y la elegida vive acá, para que cualquier pantalla la
// lea sin volver a preguntar.
//
// ## Qué se elige si no se eligió nada
//
// La que quedó guardada en este navegador, si todavía está en la lista; si no,
// la principal; si la persona no está asignada a la principal, la primera. Una
// sucursal guardada que ya no aparece —la desactivaron, o le quitaron la
// asignación— no se muestra: se cae a la principal sin avisar, porque el dato
// guardado es una comodidad y no una decisión que haya que defender.

interface Contexto {
  sucursales: readonly MiSucursal[];
  /** Nula mientras carga, o si la persona no está asignada a ninguna. */
  actual: MiSucursal | null;
  elegir: (id: string) => void;
  cargando: boolean;
}

const ContextoSucursal = createContext<Contexto | null>(null);

/// La clave de caché. Empieza igual que las de la maestra de sucursales
/// (`['sucursales', taller]`) a propósito: crear, editar o desactivar una
/// sucursal ya invalida ese prefijo, y el selector se entera sin que nadie
/// tenga que acordarse de él. Lleva el taller y la persona (regla 5).
const clave = (taller: string, persona: string) => ['sucursales', taller, 'mias', persona] as const;

/// Dónde se recuerda la elegida. Por taller y persona, por lo mismo que la
/// clave de caché: en la computadora compartida de recepción cada quien
/// encuentra la suya.
const llaveGuardada = (taller: string, persona: string) => `tallerya.admin.sucursal:${taller}:${persona}`;

// `localStorage` puede no estar —navegación privada, almacenamiento bloqueado—
// y eso no puede tumbar el app bar: sin él, simplemente no se recuerda.
function leer(llave: string): string | null {
  try {
    return localStorage.getItem(llave);
  } catch {
    return null;
  }
}

function guardar(llave: string, valor: string) {
  try {
    localStorage.setItem(llave, valor);
  } catch {
    // Sin almacenamiento, la elección dura lo que dure la pestaña.
  }
}

export function ProveedorSucursal({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const taller = useTallerDeLaCache();
  const persona = auth.user?.profile.sub ?? 'sin-sesion';
  const llave = llaveGuardada(taller, persona);

  // La elección de esta pestaña. Se inicializa con lo guardado y se vuelve a
  // leer si cambia la persona: salir y entrar con otra no hereda la elección.
  const [elegida, setElegida] = useState<{ llave: string; id: string | null }>(() => ({
    llave,
    id: leer(llave),
  }));
  const id = elegida.llave === llave ? elegida.id : leer(llave);

  const consulta = useQuery({
    queryKey: clave(taller, persona),
    enabled: auth.isAuthenticated,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/me/locations');

      // El 403 de `auth.no_membership` llega por acá. El selector no lo
      // explica —no es su lugar—: queda vacío y la pantalla que se esté viendo
      // dirá lo que pasa con su propio 403.
      if (error || !data) {
        throw new Error('No se pudieron leer las sucursales');
      }

      return data.locations;
    },
    // Cinco minutos, como los permisos: lo que la invalida de verdad es la
    // maestra de sucursales, por el prefijo de la clave.
    staleTime: 5 * 60 * 1000,
  });

  const sucursales = consulta.data ?? [];
  const actual =
    sucursales.find((s) => s.id === id) ?? sucursales.find((s) => s.isMain) ?? sucursales[0] ?? null;

  const valor: Contexto = {
    sucursales,
    actual,
    elegir: (nueva) => {
      guardar(llave, nueva);
      setElegida({ llave, id: nueva });
    },
    cargando: consulta.isPending && auth.isAuthenticated,
  };

  return <ContextoSucursal.Provider value={valor}>{children}</ContextoSucursal.Provider>;
}

export function useSucursal(): Contexto {
  const contexto = useContext(ContextoSucursal);
  if (!contexto) {
    throw new Error('useSucursal se usa dentro de ProveedorSucursal');
  }
  return contexto;
}
